import { useState, useRef } from 'preact/hooks';

interface FileUploadProps {
  onFileUploaded: (fileInfo: any) => void;
  disabled?: boolean;
}

export default function FileUpload({ onFileUploaded, disabled = false }: FileUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // ファイルサイズチェック（20MB）
    if (file.size > 20 * 1024 * 1024) {
      alert('ファイルサイズは20MB以下にしてください');
      return;
    }

    // ファイルタイプチェック
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/quicktime',
      'audio/webm', 'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/ogg',
      'text/plain', 'application/pdf'
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('サポートされていないファイル形式です');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
      const response = await fetch(`${apiBaseUrl}/api/upload`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        onFileUploaded({
          ...result.file,
          type: getFileCategory(file.type)
        });
      } else {
        alert(result.error || 'ファイルアップロードに失敗しました');
      }
    } catch (error) {
      console.error('File upload error:', error);
      alert('ファイルアップロード中にエラーが発生しました');
    } finally {
      setUploading(false);
    }
  };

  const getFileCategory = (mimeType: string): 'image' | 'video' | 'audio' | 'document' => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const files = target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  return (
    <div className="file-upload-container">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,audio/*,.txt,.pdf,.doc,.docx"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        disabled={disabled || uploading}
      />
      
      <div
        className={`file-drop-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        style={{
          border: '2px dashed #d1d5db',
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center',
          cursor: disabled || uploading ? 'not-allowed' : 'pointer',
          backgroundColor: dragOver ? '#f3f4f6' : '#fafafa',
          borderColor: dragOver ? '#3b82f6' : '#d1d5db',
          transition: 'all 0.2s ease'
        }}
      >
        {uploading ? (
          <div style={{ color: '#6b7280' }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 12px'
            }}></div>
            <p style={{ margin: 0, fontSize: '14px' }}>アップロード中...</p>
          </div>
        ) : (
          <div style={{ color: '#6b7280' }}>
            <svg
              style={{ width: '32px', height: '32px', margin: '0 auto 12px', display: 'block' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '500' }}>
              ファイルをドラッグ&ドロップまたはクリックして選択
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
              画像、動画、音声、テキストファイル（最大20MB）
            </p>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .file-drop-zone:hover {
          border-color: #3b82f6;
          background-color: #f8fafc;
        }
        
        .file-drop-zone.drag-over {
          border-color: #3b82f6;
          background-color: #eff6ff;
        }
        
        .file-drop-zone.uploading {
          pointer-events: none;
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
} 